import { isGa4MeasurementId, type SiteSettings } from "@diality/contracts";
import type { Analytics } from "./analytics";
import {
  CONSENT_CHANGE_EVENT,
  clearAnalyticsCookies,
  hasAnalyticsConsent,
  readAnalyticsConsent,
} from "./consent";
import { Ga4Adapter } from "./ga4-adapter";
import { AnalyticsSession } from "./session";
import type { AnalyticsPage } from "./page-context";

interface RuntimeConfig {
  analytics?: { ga4MeasurementId?: string };
}

/** Explicit blank/invalid settings never fall through to a deployment ID. */
export async function resolveMeasurementId(
  settings: Pick<SiteSettings, "ga4MeasurementId"> | null,
  legacy: () => Promise<unknown>,
): Promise<string | undefined> {
  if (!settings) return undefined;
  const value =
    settings.ga4MeasurementId === undefined
      ? await legacy().catch(() => undefined)
      : settings.ga4MeasurementId;
  return isGa4MeasurementId(value) && value ? value : undefined;
}

async function legacyMeasurementId(): Promise<unknown> {
  try {
    const response = await fetch("/config.json");
    if (response.ok) {
      const config = (await response.json()) as RuntimeConfig;
      if (config.analytics?.ga4MeasurementId !== undefined)
        return config.analytics.ga4MeasurementId;
    }
  } catch {
    // Local and acceptance builds can supply the legacy value through Vite.
  }
  return import.meta.env.VITE_GA4_MEASUREMENT_ID;
}

let session: AnalyticsSession | undefined;

/** Called only after a marketing route commits. Settings are fixed per document. */
export function visitAnalyticsPage(
  settings: SiteSettings | null,
  page: AnalyticsPage,
): void {
  if (!session) {
    const current = new AnalyticsSession(hasAnalyticsConsent());
    session = current;
    const syncConsent = () => {
      current.setConsent(hasAnalyticsConsent());
      if (readAnalyticsConsent(document.cookie) === false)
        clearAnalyticsCookies();
    };
    window.addEventListener(CONSENT_CHANGE_EVENT, syncConsent);
    if (!hasAnalyticsConsent()) clearAnalyticsCookies();
    void resolveMeasurementId(settings, legacyMeasurementId).then((id) => {
      if (id) current.ready(new Ga4Adapter(id));
    });
  }
  session.visit(page, hasAnalyticsConsent());
}

export function getAnalytics(): Analytics | undefined {
  const current = session;
  if (!current) return undefined;
  return {
    publish(event) {
      // Re-read for expiry or a consent change made in another tab.
      current.setConsent(hasAnalyticsConsent());
      current.publish(event);
    },
  };
}
